import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';

import { activeNetworkSelector, userSelector } from 'redux/selectors';
import {
  useCreateUserGoogleAccountMutation,
  useDeleteUserGoogleAccountMutation,
  useLazyGetUserGoogleAccountsQuery,
} from 'services/userGoogleAccountsV2';
import { useUpdateLinkedinSyncStatusMutation, useDisconnectLinkedinSyncMutation } from 'services/userV2';
import { useGoogleSync } from 'hooks/useGoogleSync';
import { useHasGoogleSyncFeature } from 'hooks/useHasGoogleSyncFeature';

export const useIntegrationsPage = ({ network: networkOverride, onGoogleSyncComplete } = {}) => {
  const user = useSelector(userSelector);
  const activeNetworkFromStore = useSelector(activeNetworkSelector);
  const activeNetwork = networkOverride || activeNetworkFromStore;
  const hasGoogleSyncFeature = useHasGoogleSyncFeature({ network: activeNetwork });

  // LinkedIn sync
  const linkedinSync = useMemo(
    () => ({
      syncStatus: user.linkedinSync?.syncStatus || 'not_connected',
      lastSyncedAt: user.linkedinSync?.lastSyncedAt,
      contactsSynced: user.linkedinSync?.contactsSynced || 0,
    }),
    [user.linkedinSync],
  );
  const [updateLinkedinSyncStatus, { isLoading: isUpdatingLinkedinSync }] = useUpdateLinkedinSyncStatusMutation();
  const [disconnectLinkedinSync, { isLoading: isDisconnectingLinkedin }] = useDisconnectLinkedinSyncMutation();

  // Google sync
  const [createGoogleAccount] = useCreateUserGoogleAccountMutation();
  const [deleteGoogleAccount, { isLoading: isDeletingGoogleAccount }] = useDeleteUserGoogleAccountMutation();
  const { googleSync, isConnected: isGoogleConnected, refetch: refetchGoogleSync } = useGoogleSync();
  const [getGoogleAccountsStatus] = useLazyGetUserGoogleAccountsQuery();
  const pollingRef = useRef(null);

  // Poll for Google sync status when syncing
  useEffect(() => {
    let pendingQuery = null;

    const cleanup = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pendingQuery?.abort();
    };

    if (googleSync.syncStatus !== 'syncing') {
      cleanup();
      return () => {};
    }

    pollingRef.current = setInterval(() => {
      pendingQuery = getGoogleAccountsStatus();
      pendingQuery.then((res) => {
        const accounts = res?.data || [];
        const account = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
        if (account?.syncStatus && account.syncStatus !== 'syncing') {
          refetchGoogleSync();
          cleanup();
        }
      });
    }, 3000);

    return cleanup;
  }, [googleSync.syncStatus, getGoogleAccountsStatus, refetchGoogleSync]);

  const onGoogleSuccess = async ({ code }) => {
    try {
      await createGoogleAccount({ code }).unwrap();
      toast('Settings updated', {
        icon: 'success',
        id: 'google-account-connected',
      });
      onGoogleSyncComplete?.();
    } catch (error) {
      const message = error?.data?.error || 'Failed to connect Google account';
      toast(message, { icon: 'error' });
    }
  };

  const onGoogleError = (error) => {
    let message = 'Failed to start Google authorization';

    if (error?.error === 'access_denied') {
      message =
        'Google authorization was cancelled. Please try again and grant the required permissions to sync your contacts.';
    } else if (error?.error) {
      message = error.error;
    }

    toast(message, { icon: 'error' });
  };

  const googleLogin = useGoogleLogin({
    onSuccess: onGoogleSuccess,
    onError: onGoogleError,
    flow: 'auth-code',
    redirect_uri: 'postmessage',
    scope: 'https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/contacts.other.readonly',
  });

  const getLinkedinSyncLink = useCallback(
    (status = 'sync') =>
      user.isOnlyAlly || !activeNetwork?.slug
        ? `/linkedin/${status}`
        : `/networks/${activeNetwork.slug}/linkedin/${status}`,
    [activeNetwork?.slug, user.isOnlyAlly],
  );

  // Anchor the "My networks" CTA to whichever Settings tree the user is
  // currently in (network-scoped vs ally-scoped) instead of hard-coding the
  // network slug, so the link stays consistent with the page they navigated
  // from and matches the pattern used by myNetworks/index.jsx.
  const settingsBaseMatch = useRouteMatch(['/networks/:networkSlug/settings', '/settings']);
  const myNetworksLink = `${(settingsBaseMatch?.url || '/settings').replace(/\/$/, '')}/my-networks`;

  // Treat "any integration is connected" as true when LinkedIn is wired up OR
  // (only when the google_sync feature is on for this network) when Google is
  // wired up. Mirrors contactsExtended/importButton so the page-level warning
  // and the in-table CTA agree on what "connected" means — read the raw redux
  // `user.linkedinSync` here (importButton does the same) rather than the
  // hook's normalized `linkedinSync`, which is always a truthy object.
  const isLinkedinConnected = Boolean(user.linkedinSync);
  const isAnyConnected = isLinkedinConnected || (hasGoogleSyncFeature && isGoogleConnected);

  // Gate on activeNetwork being present so the warning never flashes during
  // the brief window before the network store is hydrated (when activeNetwork
  // is null but `useHasGoogleSyncFeature()` can still fall back to the user's
  // collections and report true).
  const shouldShowShareContactsWarning = Boolean(activeNetwork) && isAnyConnected && !activeNetwork.shareContacts;

  const handleConfirmRemoveLinkedin = useCallback(async () => {
    try {
      await disconnectLinkedinSync().unwrap();
      toast('Settings updated', {
        icon: 'success',
        id: 'linkedin-sync-disconnected',
      });
    } catch (error) {
      const message = error?.data?.error || 'Failed to disconnect LinkedIn sync';
      toast(message, { icon: 'error' });
    }
  }, [disconnectLinkedinSync]);

  const handleConfirmRemoveGoogle = useCallback(async () => {
    try {
      if (!googleSync?.id) return;
      await deleteGoogleAccount({ id: googleSync.id }).unwrap();
      toast('Settings updated', {
        icon: 'success',
        id: 'google-account-removed',
      });
    } catch (error) {
      const message = error?.data?.error || 'Failed to remove Google account';
      toast(message, { icon: 'error' });
    }
  }, [deleteGoogleAccount, googleSync?.id]);

  return {
    user,
    activeNetwork,
    googleSync,
    googleLogin,
    getLinkedinSyncLink,
    handleConfirmRemoveGoogle,
    handleConfirmRemoveLinkedin,
    hasGoogleSyncFeature,
    isDisconnectingLinkedin,
    isDeletingGoogleAccount,
    linkedinSync,
    isUpdatingLinkedinSync,
    updateLinkedinSyncStatus,
    isAnyConnected,
    myNetworksLink,
    shouldShowShareContactsWarning,
  };
};
